import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEVICE_TYPES } from '@bre-ticketing/contracts';
import type { ApiError, DeviceRow, HardwareItem, HardwareOverview, HardwareState, PrintQueueJob } from '../api';
import { boxOfficeEndpoints, endpoints, hardwareEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';

/**
 * Is the hardware ready (items 81 and 310 — D-307).
 *
 * One screen, on purpose. Printers, bridges, readers, scanners and the job
 * queue were each somewhere else, so the question somebody asks once — before a
 * door, at five to seven — took five places to answer.
 *
 * The order is the order of the errand: **what is wrong**, then the **queue**,
 * because a stuck queue is the thing that actually stops a line moving, then
 * everything else for reference. A screen that opens on an inventory makes you
 * read the whole inventory to find out nothing is wrong.
 */

const readableType = (type: string) => type.replaceAll('_', ' ').toLowerCase();

const STATE_TONE: Record<HardwareState, Tone> = { READY: 'ok', ATTENTION: 'warn', DOWN: 'bad', OFF: 'neutral' };
const STATE_WORD: Record<HardwareState, string> = { READY: 'ready', ATTENTION: 'check', DOWN: 'down', OFF: 'off' };

const KIND_WORD: Record<HardwareItem['kind'], string> = {
  PRINT_BRIDGE: 'print bridge',
  BOX_OFFICE_TERMINAL: 'window terminal',
  SCANNER: 'scanner',
  KIOSK: 'kiosk',
  PRINTER: 'printer',
  CARD_READER: 'card reader',
};

/** Bridges first, then what hangs off them, then the rest — the order somebody troubleshoots in. */
const KIND_ORDER: HardwareItem['kind'][] = ['PRINT_BRIDGE', 'PRINTER', 'BOX_OFFICE_TERMINAL', 'CARD_READER', 'SCANNER', 'KIOSK'];

function howLong(seconds: number): string {
  if (seconds < 90) return `${seconds} seconds`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} minutes`;
  return `${Math.round(seconds / 3600)} hours`;
}

export default function HardwarePage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ code: string; expiresAt: string; deviceName: string } | null>(null);
  const [registering, setRegistering] = useState(false);

  const overview = useQuery<HardwareOverview, ApiError>({
    queryKey: ['hardware'],
    queryFn: hardwareEndpoints.overview,
    // A hardware screen that is out of date is worse than no hardware screen:
    // somebody stands in front of a dead printer reading that it is fine.
    refetchInterval: 15_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['hardware'] });
  const onError = (cause: ApiError) => setError(cause.message);
  const onDone = () => {
    setError(null);
    void refresh();
  };

  const testPage = useMutation({ mutationFn: hardwareEndpoints.testPage, onSuccess: onDone, onError });
  /*
   * Pairing, and unpairing, live here rather than on a screen of their own: a
   * device that will not check in and a device that was never paired are the
   * same errand, and sending somebody to a second screen to finish it is how
   * the first screen stops being the one place to look.
   */
  const pairingCode = useMutation({
    mutationFn: (item: HardwareItem) => endpoints.pairingCode(item.id).then((result) => ({ result, item })),
    onSuccess: ({ result, item }) => {
      setIssued({ code: result.code, expiresAt: result.expires_at, deviceName: item.name });
      onDone();
    },
    onError,
  });
  const revoke = useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => endpoints.revokeDevice(id, reason), onSuccess: onDone, onError });
  const retry = useMutation({ mutationFn: hardwareEndpoints.retryJob, onSuccess: onDone, onError });
  const cancel = useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => hardwareEndpoints.cancelJob(id, reason), onSuccess: onDone, onError });

  if (!can('devices.manage')) return <NotPermitted permission="devices.manage" />;

  const data = overview.data;
  const items = data?.items ?? [];
  const wrong = items.filter((item) => item.state === 'DOWN' || item.state === 'ATTENTION');
  const byId = new Map(items.map((item) => [item.id, item]));
  const sorted = [...items].sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.name.localeCompare(b.name));

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {issued ? (
        <div className="rounded-lg border border-gold/40 bg-warn-soft px-5 py-4">
          <p className="text-xs font-semibold tracking-wide text-warn uppercase">Pairing code — {issued.deviceName}</p>
          <p className="tabular mt-1 text-3xl font-bold tracking-[0.2em] text-ink-900">{issued.code}</p>
          <p className="mt-1 text-sm text-ink-600">
            Enter this on the device now. It works once, expires {formatWhen(issued.expiresAt)}, and cannot be shown again.
          </p>
          <Button className="mt-3" onClick={() => setIssued(null)}>
            Done
          </Button>
        </div>
      ) : null}

      {registering ? (
        <RegisterDevice
          onCancel={() => setRegistering(false)}
          onRegistered={() => {
            setRegistering(false);
            onDone();
          }}
          onError={onError}
        />
      ) : null}

      <Panel
        title={wrong.length === 0 ? 'Everything is reporting normally' : `${wrong.length} ${wrong.length === 1 ? 'thing needs' : 'things need'} attention`}
        description={
          data
            ? `A print bridge checks in every ${data.rules.heartbeat_seconds} seconds and is called down after ${data.rules.silent_after_seconds}. Scanners and window terminals are only heard from when somebody uses one, so a quiet terminal is not a fault.`
            : 'Loading…'
        }
        actions={<Button onClick={() => void refresh()}>Refresh</Button>}
      >
        {overview.isPending ? (
          <p className="px-5 py-4 text-sm text-ink-500">Loading…</p>
        ) : wrong.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-600">Nothing is down, and nothing is half-configured.</p>
        ) : (
          <div className="divide-y divide-line">
            {wrong.map((item) => (
              <div key={item.id} className="px-5 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={STATE_TONE[item.state]}>{STATE_WORD[item.state]}</Badge>
                  <span className="text-sm font-medium text-ink-900">{item.name}</span>
                  <span className="text-xs text-ink-400">
                    {KIND_WORD[item.kind]}
                    {item.venue_name ? ` · ${item.venue_name}` : ''}
                    {item.parent_id ? ` · via ${byId.get(item.parent_id)?.name ?? 'a bridge'}` : ''}
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink-600">{item.detail}</p>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Waiting to print"
        description={
          data
            ? `${data.queue.queued} queued, ${data.queue.leased} with a bridge, ${data.queue.failed} failed.${
                data.queue.oldest_queued_seconds !== null ? ` The oldest has waited ${howLong(data.queue.oldest_queued_seconds)}.` : ''
              }`
            : ' '
        }
      >
        <Table headers={['Job', 'Printer', 'Status', 'Waiting since', '']}>
          {overview.isPending ? (
            <EmptyRow columns={5}>Loading…</EmptyRow>
          ) : (data?.queue.jobs ?? []).length === 0 ? (
            <EmptyRow columns={5}>Nothing is waiting. Everything queued has printed.</EmptyRow>
          ) : (
            (data?.queue.jobs ?? []).map((job) => (
              <JobRow
                key={job.id}
                job={job}
                busy={retry.isPending || cancel.isPending}
                onRetry={() => retry.mutate(job.id)}
                onCancel={() => {
                  const reason = window.prompt(`Why is this ${job.job_type.replaceAll('_', ' ').toLowerCase()} being stopped?`);
                  if (reason) cancel.mutate({ id: job.id, reason });
                }}
              />
            ))
          )}
        </Table>
      </Panel>

      <Panel
        title="Everything registered"
        description="What exists, where it is, and when it was last heard from."
        actions={
          !registering ? (
            <Button variant="primary" onClick={() => setRegistering(true)}>
              Register a device
            </Button>
          ) : null
        }
      >
        <Table headers={['Device', 'Kind', 'State', 'Last heard from', '']}>
          {overview.isPending ? (
            <EmptyRow columns={5}>Loading…</EmptyRow>
          ) : sorted.length === 0 ? (
            <EmptyRow columns={5}>No hardware is registered yet. Register a device, then add its printers to it.</EmptyRow>
          ) : (
            sorted.map((item) => (
              <tr key={item.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className={`font-medium text-ink-900 ${item.parent_id ? 'pl-4' : ''}`}>{item.name}</div>
                  <div className="text-xs text-ink-400">
                    {[item.venue_name, ...Object.entries(item.extra).filter(([, value]) => value !== null && value !== '').map(([key, value]) => `${key} ${value}`)]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{KIND_WORD[item.kind]}</td>
                <td className="px-5 py-3">
                  <Badge tone={STATE_TONE[item.state]}>{STATE_WORD[item.state]}</Badge>
                  <div className="mt-1 max-w-sm text-xs text-ink-500">{item.detail}</div>
                </td>
                <td className="tabular px-5 py-3 text-xs text-ink-400">
                  {formatWhen(item.last_seen_at)}
                  {/* Said explicitly, because an old timestamp next to a green badge otherwise reads as a contradiction. */}
                  {!item.reports_in ? <div className="text-ink-400">only when used</div> : null}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {item.kind === 'PRINTER' && item.state !== 'OFF' ? (
                    <Button disabled={testPage.isPending} onClick={() => testPage.mutate(item.id)}>
                      Test page
                    </Button>
                  ) : null}
                  {/* A card reader belongs to Stripe and a printer to its bridge; only a paired device has a token to hand out or take away. */}
                  {item.kind !== 'PRINTER' && item.kind !== 'CARD_READER' && item.state !== 'OFF' ? (
                    <>
                      <Button disabled={pairingCode.isPending} onClick={() => pairingCode.mutate(item)}>
                        {item.state === 'ATTENTION' ? 'Pairing code' : 'New pairing code'}
                      </Button>
                      <Button
                        variant="danger"
                        className="ml-2"
                        onClick={() => {
                          const reason = window.prompt(`Why is ${item.name} being revoked?`);
                          if (reason) revoke.mutate({ id: item.id, reason });
                        }}
                      >
                        Revoke
                      </Button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function JobRow({ job, busy, onRetry, onCancel }: { job: PrintQueueJob; busy: boolean; onRetry: () => void; onCancel: () => void }) {
  const tone: Tone = job.status === 'FAILED' ? 'bad' : job.status === 'LEASED' ? 'accent' : 'warn';
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-5 py-3">
        <div className="text-sm font-medium text-ink-900">{job.job_type.replaceAll('_', ' ').toLowerCase()}</div>
        <div className="tabular text-xs text-ink-400">{job.order_number ? `order ${job.order_number}` : job.id.slice(0, 8)}</div>
      </td>
      <td className="px-5 py-3 text-sm text-ink-600">{job.printer_name}</td>
      <td className="px-5 py-3">
        <Badge tone={tone}>{job.held ? 'printing now' : job.status.toLowerCase()}</Badge>
        {job.failure_reason ? <div className="mt-1 max-w-sm text-xs text-bad">{job.failure_reason}</div> : null}
        {job.attempt_count > 0 ? (
          <div className="text-xs text-ink-400">
            {job.attempt_count} of {job.max_attempts} tries
          </div>
        ) : null}
      </td>
      <td className="tabular px-5 py-3 text-xs text-ink-400">{formatWhen(job.created_at)}</td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {/*
          A job a bridge is holding may be halfway through a printer. Both
          buttons are refused by the API for exactly that reason, so they are
          not offered here either — an offered button that always fails is worse
          than no button.
        */}
        {job.held ? (
          <span className="text-xs text-ink-400">with the printer</span>
        ) : (
          <>
            {job.status === 'FAILED' ? (
              <Button disabled={busy} onClick={onRetry}>
                Send it again
              </Button>
            ) : null}
            <Button variant="danger" className="ml-2" disabled={busy} onClick={onCancel}>
              Stop it
            </Button>
          </>
        )}
      </td>
    </tr>
  );
}

function RegisterDevice({
  onCancel,
  onRegistered,
  onError,
}: {
  onCancel: () => void;
  onRegistered: () => void;
  onError: (error: ApiError) => void;
}) {
  // DEVICE_TYPES is a non-empty tuple, but noUncheckedIndexedAccess types the
  // first element as possibly undefined.
  const [deviceType, setDeviceType] = useState<string>(DEVICE_TYPES[0] as string);
  const [name, setName] = useState('');
  const [venueId, setVenueId] = useState('');
  const [registerId, setRegisterId] = useState('');
  const venues = useQuery({ queryKey: ['venues'], queryFn: endpoints.venues });
  const config = useQuery({ queryKey: ['box-office', venueId], queryFn: () => boxOfficeEndpoints.venue(venueId), enabled: Boolean(venueId) });

  const create = useMutation({
    mutationFn: () => endpoints.createDevice({ device_type: deviceType, name: name.trim(), venue_id: venueId || null, register_id: deviceType === 'BOX_OFFICE_TERMINAL' ? registerId || null : null }),
    onSuccess: onRegistered,
    onError,
  });

  return (
    <Panel title="Register a device" description="It starts unpaired; give it a pairing code once it is registered.">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div>
          <label htmlFor="device-type" className="text-xs font-semibold tracking-wide text-ink-400 uppercase">
            Type
          </label>
          <select
            id="device-type"
            value={deviceType}
            onChange={(event) => setDeviceType(event.target.value)}
            className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
          >
            {DEVICE_TYPES.map((type) => (
              <option key={type} value={type}>
                {readableType(type)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="device-venue" className="text-xs font-semibold tracking-wide text-ink-400 uppercase">
            Venue
          </label>
          <select id="device-venue" value={venueId} onChange={(event) => { setVenueId(event.target.value); setRegisterId(''); }} className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm">
            <option value="">—</option>
            {(venues.data?.venues ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
        {deviceType === 'BOX_OFFICE_TERMINAL' ? (
          <div>
            <label htmlFor="device-register" className="text-xs font-semibold tracking-wide text-ink-400 uppercase">
              Register
            </label>
            <select id="device-register" value={registerId} onChange={(event) => setRegisterId(event.target.value)} className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm">
              <option value="">Choose at open</option>
              {(config.data?.registers ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div>
          <label htmlFor="device-name" className="text-xs font-semibold tracking-wide text-ink-400 uppercase">
            Name
          </label>
          <input
            id="device-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Ticket Window 1"
            className="mt-1 block w-56 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </div>

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={name.trim().length === 0 || create.isPending}>
            {create.isPending ? 'Registering…' : 'Register'}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}
